/**
 * In-memory version of the import / export API (tracker item 52) for the mock
 * client. A simplified copy of the backend rules: exact column names (case-
 * and space-insensitive), required columns, duplicate detection, all-or-
 * nothing commit. The mock client hands in its data through
 * MockImportExportState.
 */
import * as types from './types';
import { IImportExportApi } from './contracts';

export interface MockImportCustomer {
  id: string;
  name: string;
  email?: string | null;
  externalRef?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null;
  status?: string;
  billingMethod?: string;
  taxExempt?: boolean;
}

export interface MockImportExportState {
  /** The signed-in user (throws like a 401 when there is none) */
  actor(): { role: string; tenantId: string; id: string; email: string };
  error(status: number, message: string, code?: string, details?: unknown): Error;
  customers(tenantId: string): MockImportCustomer[];
  properties(tenantId: string): { customerId: string; address: string; city: string; state: string; zip: string; taxRateId: string }[];
  items(tenantId: string): { name: string; itemType: string; unitPrice: string; deleted: boolean }[];
  taxRates(tenantId: string): { id: string; name: string }[];
  categories(tenantId: string): { id: string; name: string }[];
  accountNumbers(tenantId: string): { id: string; accountNumber: string; accountType: string }[];
  invoices(tenantId: string): { invoiceNumber: string; invoiceDate: string; status: string; total: string; customerName: string }[];
  payments(tenantId: string): { paymentDate: string; amount: string; status: string; customerName: string }[];
  addCustomer(tenantId: string, row: Record<string, string | boolean | null>): void;
  addProperty(tenantId: string, row: { customerId: string; billingCustomerId: string; address: string; city: string; state: string; zip: string; taxRateId: string }): void;
  addCategory(tenantId: string, name: string): string;
  addItem(tenantId: string, row: { name: string; itemType: types.ItemType; unitPrice: string; description: string | null; revenueAccountId: string; expenseAccountId: string | null; categoryId: string | null; taxable: boolean; unitOfMeasure: string }): void;
  audit(action: types.AuditAction, details: Record<string, unknown>): void;
}

const col = (name: string, required: boolean, description: string): types.ImportColumnSpec => ({ name, required, description });

export const MOCK_IMPORT_SPECS: types.ImportEntitySpec[] = [
  {
    entity: 'customers',
    label: 'Customers',
    description: 'One row per customer. Rows that match an existing customer (by reference, email, or name and street address) are skipped.',
    presets: ['STANDARD', 'QUICKBOOKS'],
    columns: [
      col('externalRef', false, "Your old system's customer number or name; unique; properties can link to it (max 100)"),
      col('name', true, 'Customer or company name (max 255)'),
      col('email', false, 'Email address; unique per company when given'),
      col('phone', false, 'Phone number'),
      col('address', false, 'Billing street address'),
      col('city', false, 'Billing city'),
      col('state', false, 'Billing state'),
      col('zip', false, 'Billing ZIP / postal code'),
      col('taxExempt', false, 'yes / no (default no)'),
      col('billingMethod', false, 'PER_VISIT (default) or CONSOLIDATED'),
      col('status', false, 'LEAD, PROSPECT, ACTIVE (default), INACTIVE or LOST'),
    ],
  },
  {
    entity: 'properties',
    label: 'Properties (service addresses)',
    description: 'One row per service address. The owner customer must already exist: link it by customerRef, customerEmail or a unique customerName.',
    presets: ['STANDARD'],
    columns: [
      col('customerRef', false, "The owner customer's externalRef"),
      col('customerEmail', false, "The owner customer's email"),
      col('customerName', false, "The owner customer's name (must be unique)"),
      col('billingCustomerRef', false, 'Who is invoiced, by externalRef (default: the owner)'),
      col('billingCustomerEmail', false, 'Who is invoiced, by email (default: the owner)'),
      col('address', true, 'Street address'),
      col('city', true, 'City'),
      col('state', true, 'State'),
      col('zip', true, 'ZIP / postal code'),
      col('taxRate', false, 'Tax rate name (default: the tax rate chosen on the upload form)'),
      col('sizeValue', false, 'Property size for price charts (with sizeUnit)'),
      col('sizeUnit', false, 'SQFT, SQYD, SQM, ACRE, HECTARE, LINEAR_FT or METER'),
    ],
  },
  {
    entity: 'items',
    label: 'Items (price list)',
    description: 'One row per service, labor, product or fee. Rows whose name matches an existing item are skipped.',
    presets: ['STANDARD'],
    columns: [
      col('name', true, 'Item name, unique (max 255)'),
      col('itemType', true, 'SERVICE, LABOR, PRODUCT or FEE'),
      col('description', false, 'Description'),
      col('unitPrice', true, 'Default price, e.g. 45.00'),
      col('unitCost', false, 'Cost per unit (products)'),
      col('unitOfMeasure', false, 'Unit, e.g. each, hour, bag (default each)'),
      col('taxable', false, 'yes / no (default yes)'),
      col('revenueAccount', false, 'Revenue GL account number (default 4000)'),
      col('expenseAccount', false, 'Expense (COGS) GL account number; products default to 5000'),
      col('category', false, 'Category name; created when missing'),
      col('defaultDurationMinutes', false, 'Typical minutes per unit, 5-1440 (SERVICE / LABOR)'),
      col('reorderPoint', false, 'Low-stock threshold (PRODUCT)'),
    ],
  },
];

const QUICKBOOKS_NAMES: Record<string, string[]> = {
  name: ['customer', 'displayname'],
  email: ['mainemail'],
  phone: ['mainphone'],
  address: ['billingstreet'],
  city: ['billingcity'],
  state: ['billingstate'],
  zip: ['billingzip'],
};

/** Minimal RFC 4180 reader (quotes, doubled quotes, CRLF / LF, BOM). */
export function parseMockCsv(text: string): string[][] {
  const input = text.replace(/^﻿/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (quoted) {
      if (ch === '"' && input[i + 1] === '"') { cell += '"'; i++; } else if (ch === '"') quoted = false; else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && input[i + 1] === '\n') i++;
      row.push(cell); cell = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else cell += ch;
  }
  if (quoted) throw new Error('A quoted cell is never closed');
  row.push(cell);
  if (row.some((c) => c.trim() !== '')) rows.push(row);
  return rows;
}

const key = (value: unknown) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const headerKey = (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, '');

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  let out = String(value);
  if (/^[=+\-@\t\r]/.test(out) && !/^-?\d+(\.\d+)?$/.test(out)) out = `'${out}`;
  return /[",\r\n]/.test(out) ? `"${out.replace(/"/g, '""')}"` : out;
}
const csvText = (rows: unknown[][]) => '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
const csvBlob = (rows: unknown[][]) => new Blob([csvText(rows)], { type: 'text/csv;charset=utf-8' });

async function readFile(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/** A simple stand-in for the backend's SHA-256 (the mock only compares it). */
function mockDigest(text: string): string {
  let h1 = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) h1 = Math.imul(h1 ^ text.charCodeAt(i), 16777619) >>> 0;
  return (h1.toString(16).padStart(8, '0')).repeat(8);
}

interface MockPlan {
  validation: types.ImportValidation;
  creates: { rowNumber: number; values: Record<string, string> }[];
}

export function createMockImportExport(state: MockImportExportState): IImportExportApi {
  const requireAdmin = () => {
    const actor = state.actor();
    if (actor.role !== types.UserRole.ADMIN) throw state.error(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    return actor;
  };
  const requireOffice = () => {
    const actor = state.actor();
    if (actor.role !== types.UserRole.ADMIN && actor.role !== types.UserRole.OFFICE_MANAGER) {
      throw state.error(403, 'Insufficient permissions', 'INSUFFICIENT_PERMISSIONS');
    }
    return actor;
  };
  const specFor = (entity: types.ImportEntity) => {
    const spec = MOCK_IMPORT_SPECS.find((s) => s.entity === entity);
    if (!spec) throw state.error(404, 'Unknown import type', 'NOT_FOUND');
    return spec;
  };

  async function plan(entity: types.ImportEntity, file: File, options: types.ImportUploadOptions): Promise<MockPlan> {
    const actor = requireAdmin();
    const spec = specFor(entity);
    if (!/\.(csv|txt)$/i.test(file.name)) throw state.error(415, 'Only .csv (or .txt) files can be imported', 'UNSUPPORTED_FILE_TYPE');
    if (file.size > 10 * 1024 * 1024) throw state.error(413, 'The file is larger than 10 MB', 'FILE_TOO_LARGE');
    const text = await readFile(file);
    let rows: string[][];
    try {
      rows = parseMockCsv(text);
    } catch (e) {
      throw state.error(400, (e as Error).message, 'INVALID_CSV');
    }
    if (rows.length === 0) throw state.error(400, 'The file is empty', 'FILE_EMPTY');
    if (rows.length === 1) throw state.error(400, 'The file has a header row but no data rows', 'NO_ROWS');
    const preset = options.preset ?? 'STANDARD';
    const headers = rows[0];
    const claimed: (string | null)[] = headers.map(() => null);
    for (const column of spec.columns) {
      const candidates = [...(preset === 'QUICKBOOKS' ? QUICKBOOKS_NAMES[column.name] ?? [] : []), headerKey(column.name)];
      for (const candidate of candidates) {
        const index = headers.findIndex((h, i) => headerKey(h) === candidate && claimed[i] === null);
        if (index >= 0) { claimed[index] = column.name; break; }
      }
    }
    const present = new Set(claimed.filter(Boolean) as string[]);
    const missing = spec.columns.filter((c) => c.required && !present.has(c.name)).map((c) => c.name);
    if (missing.length) throw state.error(400, `Missing required columns: ${missing.join(', ')}`, 'MISSING_COLUMNS', { missing });
    const warnings: string[] = [];
    const ignored = headers.filter((h, i) => !claimed[i] && h.trim() !== '');
    if (ignored.length) warnings.push(`Ignored columns: ${ignored.join(', ')}`);

    const tenantId = actor.tenantId;
    const customers = state.customers(tenantId);
    const seen = new Set<string>();
    const results: types.ImportRowResult[] = [];
    const creates: MockPlan['creates'] = [];
    const newCategories: string[] = [];

    rows.slice(1).forEach((cells, index) => {
      const rowNumber = index + 2;
      const values: Record<string, string> = {};
      claimed.forEach((c, i) => { if (c) values[c] = (cells[i] ?? '').trim().replace(/^'([=+\-@])/, '$1'); });
      if (preset === 'QUICKBOOKS' && entity === 'customers' && !present.has('externalRef')) values.externalRef = values.name ?? '';
      const errors: string[] = [];
      let skipReason: string | undefined;
      for (const column of spec.columns) if (column.required && !values[column.name]) errors.push(`${column.name} is required`);
      let dupKey = '';

      if (entity === 'customers') {
        if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.push('email is not a valid email address');
        const match = customers.find((c) =>
          (values.externalRef && key(c.externalRef) === key(values.externalRef)) || (values.email && key(c.email) === key(values.email)));
        if (match) skipReason = `Matches existing customer "${match.name}"`;
        dupKey = key(values.externalRef) || key(values.email) || `${key(values.name)}|${key(values.address)}`;
      } else if (entity === 'properties') {
        const owner = customers.find((c) =>
          (values.customerRef && key(c.externalRef) === key(values.customerRef)) ||
          (!values.customerRef && values.customerEmail && key(c.email) === key(values.customerEmail)) ||
          (!values.customerRef && !values.customerEmail && values.customerName && key(c.name) === key(values.customerName)));
        if (!owner) errors.push('No matching customer (import customers first)');
        const tax = values.taxRate
          ? state.taxRates(tenantId).find((t) => key(t.name) === key(values.taxRate))
          : state.taxRates(tenantId).find((t) => t.id === options.defaultTaxRateId);
        if (!tax) errors.push(values.taxRate ? `No tax rate is named "${values.taxRate}"` : 'taxRate is required (or choose a default tax rate when uploading)');
        else values.__taxRateId = tax.id;
        if (owner) {
          values.__customerId = owner.id;
          if (state.properties(tenantId).some((p) => p.customerId === owner.id && key(p.address) === key(values.address))) {
            skipReason = `"${owner.name}" already has a property at ${values.address}`;
          }
        }
        dupKey = `${values.__customerId}|${key(values.address)}`;
      } else {
        const type = (values.itemType ?? '').trim().toUpperCase();
        if (type && !['SERVICE', 'LABOR', 'PRODUCT', 'FEE'].includes(type)) errors.push('itemType must be one of SERVICE, LABOR, PRODUCT, FEE');
        values.itemType = type;
        if (values.unitPrice && !/^\d+(\.\d{1,2})?$/.test(values.unitPrice.replace(/^\$/, ''))) {
          errors.push('unitPrice must be a number of at least 0 with at most 2 decimals');
        }
        const existing = state.items(tenantId).find((i) => key(i.name) === key(values.name));
        if (existing?.deleted) errors.push(`An item named "${existing.name}" was deleted; its name cannot be reused`);
        else if (existing) skipReason = `Matches existing item "${existing.name}"`;
        if (values.category && !state.categories(tenantId).some((c) => key(c.name) === key(values.category)) &&
          !newCategories.some((n) => key(n) === key(values.category))) {
          newCategories.push(values.category);
        }
        dupKey = key(values.name);
      }

      if (!errors.length && !skipReason && dupKey) {
        if (seen.has(dupKey)) errors.push('Same record as an earlier row of this file');
        seen.add(dupKey);
      }
      const action: types.ImportRowAction = errors.length ? 'ERROR' : skipReason ? 'SKIP' : 'CREATE';
      const shown = Object.fromEntries(Object.entries(values).filter(([k]) => !k.startsWith('__')).map(([k, v]) => [k, v || null]));
      results.push({ rowNumber, action, errors, warnings: [], ...(action === 'SKIP' ? { skipReason } : {}), values: shown });
      if (action === 'CREATE') creates.push({ rowNumber, values });
    });

    const summary = {
      totalRows: results.length,
      create: results.filter((r) => r.action === 'CREATE').length,
      skip: results.filter((r) => r.action === 'SKIP').length,
      error: results.filter((r) => r.action === 'ERROR').length,
    };
    return {
      creates,
      validation: {
        entity, preset, filename: file.name, sizeBytes: file.size, fileSha256: mockDigest(text), encoding: 'utf-8',
        columns: headers.map((header, i) => ({ header, column: claimed[i] })), warnings, summary,
        rows: results.filter((r, i) => r.action === 'ERROR' || i < 100), rowsTruncated: results.length > 100 && summary.error < results.length,
        newCategories: entity === 'items' ? newCategories : [],
      },
    };
  }

  return {
    async listImportSpecs() {
      requireAdmin();
      return MOCK_IMPORT_SPECS;
    },
    async downloadImportTemplate(entity) {
      requireAdmin();
      return { blob: csvBlob([specFor(entity).columns.map((c) => c.name)]), filename: `${entity}-import-template.csv` };
    },
    async validateImport(entity, file, options = {}) {
      return (await plan(entity, file, options)).validation;
    },
    async commitImport(entity, file, options = {}) {
      const { validation, creates } = await plan(entity, file, options);
      if (options.expectedSha256 && options.expectedSha256 !== validation.fileSha256) {
        throw state.error(409, 'This is not the file that was checked. Run the check again before importing.', 'FILE_CHANGED');
      }
      if (validation.summary.error > 0) {
        throw state.error(422, `${validation.summary.error} rows have errors; nothing was imported`, 'IMPORT_HAS_ERRORS', {
          summary: validation.summary, rows: validation.rows.filter((r) => r.action === 'ERROR'),
        });
      }
      const tenantId = state.actor().tenantId;
      const categoryIds = new Map<string, string>();
      for (const name of validation.newCategories) categoryIds.set(key(name), state.addCategory(tenantId, name));
      const accounts = state.accountNumbers(tenantId);
      for (const { values } of creates) {
        if (entity === 'customers') {
          state.addCustomer(tenantId, {
            name: values.name, externalRef: values.externalRef || null, email: values.email || null, phone: values.phone || null,
            address: values.address || null, city: values.city || null, state: values.state || null, zip: values.zip || null,
            taxExempt: /^(y|yes|true|1)$/i.test(values.taxExempt ?? ''),
            billingMethod: (values.billingMethod || 'PER_VISIT').toUpperCase(), status: (values.status || 'ACTIVE').toUpperCase(),
          });
        } else if (entity === 'properties') {
          state.addProperty(tenantId, {
            customerId: values.__customerId, billingCustomerId: values.__customerId, address: values.address, city: values.city,
            state: values.state, zip: values.zip, taxRateId: values.__taxRateId,
          });
        } else {
          const account = (n: string | undefined, fallback: string | null) => {
            const number = n || fallback;
            return number ? accounts.find((a) => a.accountNumber === number)?.id ?? null : null;
          };
          const category = values.category
            ? state.categories(tenantId).find((c) => key(c.name) === key(values.category))?.id ?? categoryIds.get(key(values.category)) ?? null
            : null;
          state.addItem(tenantId, {
            name: values.name, itemType: values.itemType as types.ItemType, unitPrice: Number(values.unitPrice.replace(/^\$/, '')).toFixed(2),
            description: values.description || null, revenueAccountId: account(values.revenueAccount, '4000') ?? '',
            expenseAccountId: account(values.expenseAccount, values.itemType === 'PRODUCT' ? '5000' : null), categoryId: category,
            taxable: !/^(n|no|false|0)$/i.test(values.taxable ?? ''), unitOfMeasure: values.unitOfMeasure || 'each',
          });
        }
      }
      state.audit('data.import', { entity, filename: file.name, created: creates.length, skipped: validation.summary.skip });
      return {
        entity, filename: file.name, fileSha256: validation.fileSha256, totalRows: validation.summary.totalRows,
        created: creates.length, skipped: validation.summary.skip, categoriesCreated: validation.newCategories.length,
        skippedRows: validation.rows.filter((r) => r.action === 'SKIP').map((r) => ({ rowNumber: r.rowNumber, reason: r.skipReason ?? '' })),
      };
    },
    async downloadExport(dataset, filters = {}) {
      const actor = requireOffice();
      const tenantId = actor.tenantId;
      for (const value of [filters.from, filters.to]) {
        if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw state.error(400, 'from / to must be a date (YYYY-MM-DD)', 'INVALID_FILTER');
      }
      if (filters.from && filters.to && filters.from > filters.to) throw state.error(400, 'from must be on or before to', 'INVALID_FILTER');
      const inRange = (date: string) => (!filters.from || date.slice(0, 10) >= filters.from) && (!filters.to || date.slice(0, 10) <= filters.to);
      let rows: unknown[][];
      switch (dataset) {
        case 'customers':
        case 'quickbooks-customers':
          rows = [specFor('customers').columns.map((c) => c.name), ...state.customers(tenantId).map((c) => [
            c.externalRef, c.name, c.email, c.phone, c.address, c.city, c.state, c.zip, c.taxExempt ? 'yes' : 'no', c.billingMethod, c.status,
          ])];
          break;
        case 'properties':
          rows = [['customerName', 'address', 'city', 'state', 'zip'], ...state.properties(tenantId).map((p) => [
            state.customers(tenantId).find((c) => c.id === p.customerId)?.name, p.address, p.city, p.state, p.zip,
          ])];
          break;
        case 'items':
          rows = [['name', 'itemType', 'unitPrice'], ...state.items(tenantId).filter((i) => !i.deleted).map((i) => [i.name, i.itemType, i.unitPrice])];
          break;
        case 'invoices':
        case 'quickbooks-invoices':
          rows = [['invoiceNumber', 'invoiceDate', 'status', 'customerName', 'total'], ...state.invoices(tenantId)
            .filter((i) => inRange(i.invoiceDate)).map((i) => [i.invoiceNumber, i.invoiceDate.slice(0, 10), i.status, i.customerName, i.total])];
          break;
        case 'payments':
          rows = [['paymentDate', 'amount', 'status', 'customerName'], ...state.payments(tenantId)
            .filter((p) => inRange(p.paymentDate)).map((p) => [p.paymentDate.slice(0, 10), p.amount, p.status, p.customerName])];
          break;
        default:
          rows = [['id', 'status']];
      }
      state.audit('data.export', { dataset, from: filters.from ?? null, to: filters.to ?? null });
      return { blob: csvBlob(rows), filename: `${dataset}-${new Date().toISOString().slice(0, 10)}.csv` };
    },
  };
}
